import { useMemo, useState } from "react";

import {
  InvestorCodeDialog,
  RetireInvestorCodeDialog,
} from "../../../components/portfolio-risk-concentration/InvestorCodeDialog";
import { PageHeader } from "../../../components/portfolio-risk-concentration/PageHeader";
import {
  AddResidualCodeDialog,
  ResidualCodeApprovalQueue,
  ResidualCodeDialog,
  ResidualCodeLog,
  ResidualCodeText,
} from "../../../components/portfolio-risk-concentration/ResidualCodeGovernance";
import { ManageLimitsShell } from "../../../components/portfolio-risk-concentration/StepShell";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Label } from "../../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Textarea } from "../../../ui/textarea";
import { cn } from "../../../utils/common/cn";
import { hasTier } from "../../../components/portfolio-risk-concentration/access-control";
import {
  listingChangeSummary,
  LISTING_KIND_LABEL,
  useInvestorListingProposals,
  useInvestorListings,
  useProposeInvestorListing,
  useReviewInvestorListing,
  type InvestorListing,
  type InvestorListingChangeKind,
  type InvestorListingProposal,
} from "../../../utils/portfolio-risk-concentration/api/investorListing";
import {
  useProposeResidualCode,
  useResidualCodeProposals,
  useResidualCodes,
  useReviewResidualCode,
  type ResidualCode,
  type ResidualCodeProposal,
} from "../../../utils/portfolio-risk-concentration/api/residualCodes";

// Real server-side pagination now (limit/offset sent to the backend, one page's data back) --
// same "X-Y of Z" + Prev/Next footer ContractValuationSettings.tsx's FairValueLog established,
// now backed by a real page fetch instead of a client-side slice of the whole table.
const PAGE_SIZE = 10;

/**
 * Manage Investors — what each raw investor code actually MEANS.
 *
 * ── Why it sits beside Manage Capital ───────────────────────────────────────────────────
 * Manage Capital governs WHICH codes constitute a capital source, and shows them as bare
 * numeric chips. This governs what those numbers are: code 20 is "NER 13-1", a term
 * securitization paid off in 2017. Same page shape, same propose -> approve -> log mechanics,
 * because it is the same people making the same kind of decision about the same codes.
 *
 * ── Active and inactive, without a third status ─────────────────────────────────────────
 * A code is active when it has a LIVE row and inactive when it does not. Retiring proposes a
 * RETIRE, which on approval closes the live row and inserts no successor; reactivating
 * proposes an ordinary LISTING change, which finds no incumbent and is derived as an ADD.
 * "Show retired" is therefore not a cosmetic filter — it is the only way to reach a retired
 * code at all, since the default view is live-only.
 */
export default function ManageInvestorsPage() {
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const [investorOffset, setInvestorOffset] = useState(0);

  const listingsQ = useInvestorListings(includeSuperseded, {
    limit: PAGE_SIZE,
    offset: investorOffset,
  });
  // Live-only, up to 100 -- independent of the table's own page/toggle above, so
  // InvestorCodeDialog can detect an adjustment for ANY code, not just the ones on the
  // currently visible page. Same "small governance table, fetch it all for a lookup" idiom
  // ContractValuationSettings.tsx's usePendingFairValueSpreadBatches uses.
  const allLiveListingsQ = useInvestorListings(false, { limit: 100, offset: 0 });
  // The approval queue needs every PENDING proposal, not just one page's worth, to badge every
  // code on the table above correctly -- same reasoning, same {limit:100} idiom.
  const proposalsQueueQ = useInvestorListingProposals({ limit: 100, offset: 0 });
  const propose = useProposeInvestorListing();
  const review = useReviewInvestorListing();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editListing, setEditListing] = useState<InvestorListing | null>(null);
  const [retireTarget, setRetireTarget] = useState<InvestorListing | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Manage Residual Codes — governed like the listing above (see residualCodes.ts): a reviewer
  // proposes a Loans / Leases change, a contributor approves it, and the log keeps every one. It
  // has its own queue and log below, and shares this page's toast.
  const proposeResidual = useProposeResidualCode();
  const reviewResidual = useReviewResidualCode();
  const [residualOffset, setResidualOffset] = useState(0);
  const [residualTarget, setResidualTarget] = useState<ResidualCode | null>(null);
  const [addResidualOpen, setAddResidualOpen] = useState(false);
  // Every PENDING residual proposal, not one page's worth, so every row in the table above can show
  // its badge and hide its button -- same {limit:100} idiom as the investor queue.
  const residualPendingQ = useResidualCodeProposals({ status: "PENDING", limit: 100, offset: 0 });
  const residualPending = residualPendingQ.data?.proposals ?? [];
  const residualPendingByKey = useMemo(() => {
    const m = new Map<string, ResidualCodeProposal>();
    for (const p of residualPending) m.set(p.residualCode, p);
    return m;
  }, [residualPending]);

  const residualCodesQ = useResidualCodes({ limit: PAGE_SIZE, offset: residualOffset });
  const residualPage = residualCodesQ.data?.residualCodes ?? [];
  const residualTotal = residualCodesQ.data?.total ?? 0;

  async function onResidualReview(
    p: ResidualCodeProposal,
    decision: "APPROVED" | "REJECTED",
    notes: string,
  ) {
    const res = await reviewResidual.mutateAsync({ proposalId: p.proposalId, decision, notes });
    if (!res.ok) throw new Error(res.error || "The review could not be applied.");
    setToast(
      decision === "APPROVED"
        ? p.changeType === "ADD"
          ? `Residual code ${p.residualCode.trim()} added as ${p.newResidualType}.`
          : `Residual code ${p.residualCode.trim()} is now ${p.newResidualType}.`
        : `Proposal for residual code ${p.residualCode.trim()} rejected.`,
    );
    return res;
  }

  const pending = useMemo(
    () => (proposalsQueueQ.data?.proposals ?? []).filter((p) => p.status === "PENDING"),
    [proposalsQueueQ.data],
  );

  const canDecide = hasTier("contributor");
  // A reviewer proposes; a contributor (or above) approves. Gates the Residual Codes button, which the
  // backend enforces with require_tier on POST /residual-code-proposals.
  const canPropose = hasTier("reviewer");

  /**
   * Which kinds are already in flight per code — the per-(code, kind) lock, mirroring the
   * partial unique indexes in sql/106_. Raising a second proposal of the same kind would be
   * rejected with a 409, so the action is replaced by a badge rather than offered and refused.
   */
  const pendingKinds = useMemo(() => {
    const m = new Map<string, InvestorListingChangeKind[]>();
    for (const p of pending) {
      const held = m.get(p.investorCode) ?? [];
      if (!held.includes(p.changeKind)) held.push(p.changeKind);
      m.set(p.investorCode, held);
    }
    return m;
  }, [pending]);

  // One row per code already — GET /investor-listings collapses to the LIVE row where there is
  // one, else the latest SUPERSEDED one, in SQL now (a DISTINCT ON), so LIMIT/OFFSET can't
  // split a code's history across two pages the way a client-side collapse over one page at a
  // time would.
  const investorPage = listingsQ.data?.listings ?? [];
  const investorTotal = listingsQ.data?.total ?? 0;

  async function onReview(
    p: InvestorListingProposal,
    decision: "APPROVED" | "REJECTED",
    notes: string,
  ) {
    const res = await review.mutateAsync({ proposalId: p.proposalId, decision, notes });
    if (!res.ok) throw new Error(res.error || "The review could not be applied.");
    setToast(
      decision === "APPROVED"
        ? p.changeKind === "RETIRE"
          ? `Investor code ${p.investorCode} retired.`
          : `Investor code ${p.investorCode} is now ${p.newCodeMapping}, effective ${p.effectiveStartDate}.`
        : `Proposal for investor code ${p.investorCode} rejected.`,
    );
    return res;
  }

  return (
    <ManageLimitsShell backTo="/risk-concentrations" backLabel="← Back to Monitor">
      <PageHeader title="Manage Investors" />

      {toast && (
        <div className="mt-4 rounded-md border border-[color:var(--approve)]/40 bg-[color:var(--approve)]/10 px-3 py-2 text-xs">
          {toast}
        </div>
      )}

      {/* Surfaced rather than swallowed. A failed read leaves `data` undefined, the `?? []`
          below turns that into an empty table and an empty queue, and the page then looks
          exactly like a working page with nothing in it — which is how a mocked-out read went
          unnoticed once already. */}
      {(listingsQ.isError || proposalsQueueQ.isError) && (
        <div className="mt-4 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          Investor codes could not be loaded, so this page may be showing less than there is.
          {listingsQ.error instanceof Error ? ` ${listingsQ.error.message}` : ""}
          {proposalsQueueQ.error instanceof Error ? ` ${proposalsQueueQ.error.message}` : ""}
        </div>
      )}

      <p className="mt-4 max-w-3xl text-xs text-muted-foreground">
        What each raw investor code means. These are the same codes Manage Capital maps to a
        capital source — naming them here is what lets a reader see <em>NER 13-1</em> rather
        than <em>20</em>. Changes are proposed, reviewed and logged exactly as capital changes
        are.
      </p>

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setIncludeSuperseded((v) => !v);
            setInvestorOffset(0); // toggling changes the total row count
          }}
          title="Retired codes have no live listing, so they are hidden by default. Show them to reactivate one."
        >
          {includeSuperseded ? "Hide retired" : "Show retired"}
        </Button>
        <Button
          size="sm"
          onClick={() => {
            setEditListing(null);
            setDialogOpen(true);
          }}
        >
          + Add investor code
        </Button>
      </div>

      <div className="mt-3 overflow-hidden rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Investor code</TableHead>
              <TableHead>Mapping</TableHead>
              {/* Owned or Serviced -- edited through Adjust, like the name and note. */}
              <TableHead>Owned / Serviced</TableHead>
              {/* Read-only here: the facility this code is mapped to, from Manage Capital. */}
              <TableHead>Facility</TableHead>
              <TableHead>Notes</TableHead>
              {/* Derived, not governed: which live capital source currently names this code.
                  Here because "is anything actually using this?" is the first question asked
                  of a code someone is about to retire. */}
              <TableHead>Used by</TableHead>
              <TableHead>Effective from</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {investorTotal === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="py-6 text-center text-sm text-muted-foreground">
                  {listingsQ.isLoading
                    ? "Loading investor codes…"
                    : pending.length
                      ? // Nothing is live YET, but something is in flight. Saying "add one to
                        // get started" here reads as though the proposal just made vanished.
                        `No investor codes are live yet — ${pending.length} ${
                          pending.length === 1 ? "proposal is" : "proposals are"
                        } awaiting review below.`
                      : "No investor codes listed yet. Add one to get started."}
                </TableCell>
              </TableRow>
            )}
            {investorPage.map((l) => {
              const inFlight = pendingKinds.get(l.investorCode) ?? [];
              const isLive = l.status === "LIVE";
              return (
                <TableRow key={l.listingId}>
                  <TableCell className="font-mono tabular-nums">{l.investorCode}</TableCell>
                  <TableCell className="font-medium">{l.codeMapping}</TableCell>
                  <TableCell>
                    {l.ownership ? (
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px] uppercase tracking-[0.1em]",
                          l.ownership === "Owned"
                            ? "border-primary/50 text-primary"
                            : "border-border text-muted-foreground",
                        )}
                      >
                        {l.ownership}
                      </Badge>
                    ) : (
                      <span
                        className="text-xs text-muted-foreground"
                        title="Not set yet. Use Adjust to choose Owned or Serviced."
                      >
                        Not set
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {l.facility || "—"}
                  </TableCell>
                  <TableCell className="max-w-[260px] text-xs text-muted-foreground">
                    {l.notes || "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {l.usedBy || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{l.effectiveStartDate}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] uppercase tracking-[0.1em]",
                        isLive
                          ? "border-[color:var(--approve)]/50 text-[color:var(--approve)]"
                          : "border-muted-foreground/40 text-muted-foreground",
                      )}
                    >
                      {isLive ? "Live" : "Retired"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      {inFlight.map((k) => (
                        <Badge
                          key={k}
                          variant="outline"
                          className="border-primary/50 text-[10px] uppercase tracking-[0.1em] text-primary"
                          title={`A ${LISTING_KIND_LABEL[
                            k
                          ].toLowerCase()} proposal for this code is awaiting review.`}
                        >
                          {LISTING_KIND_LABEL[k]} pending
                        </Badge>
                      ))}
                      {isLive ? (
                        <>
                          {!inFlight.includes("LISTING") && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditListing(l);
                                setDialogOpen(true);
                              }}
                            >
                              Adjust
                            </Button>
                          )}
                          {!inFlight.includes("RETIRE") && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setRetireTarget(l)}
                            >
                              Retire
                            </Button>
                          )}
                        </>
                      ) : (
                        !inFlight.includes("LISTING") && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditListing(l);
                              setDialogOpen(true);
                            }}
                          >
                            Reactivate
                          </Button>
                        )
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {investorTotal === 0
            ? "0 of 0"
            : `${investorOffset + 1}–${Math.min(investorOffset + PAGE_SIZE, investorTotal)} of ${investorTotal}`}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={investorOffset === 0}
            onClick={() => setInvestorOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            Prev
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={investorOffset + PAGE_SIZE >= investorTotal}
            onClick={() => setInvestorOffset((o) => o + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      </div>

      <InvestorListingApprovalQueue
        proposals={pending}
        onReview={onReview}
        canDecide={canDecide}
      />

      <InvestorListingLog />

      {/* Manage Residual Codes — athena_input.residual_code_mapping: one row per residual code, no
          investor code. Only the Loans / Leases tag is edited, and it changes by proposal: the
          queue and change log follow this card. */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Manage Residual Codes</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 max-w-3xl text-xs text-muted-foreground">
            Every residual-value code, and whether it marks a contract a Loan or a Lease. A
            change, or a new code, is proposed, reviewed and logged, like the investor codes above.
          </p>

          {residualCodesQ.isError && (
            <div className="mb-3 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              Residual codes could not be loaded.
            </div>
          )}

          {canPropose && (
            <div className="mb-3 flex justify-end">
              <Button size="sm" onClick={() => setAddResidualOpen(true)}>
                + Add residual code
              </Button>
            </div>
          )}

          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Residual Code</TableHead>
                  <TableHead>Residual Type</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {residualTotal === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="py-6 text-center text-sm text-muted-foreground">
                      {residualCodesQ.isLoading
                        ? "Loading residual codes…"
                        : "No residual codes found."}
                    </TableCell>
                  </TableRow>
                )}
                {residualPage.map((r) => {
                  const pendingProposal = residualPendingByKey.get(r.residualCode);
                  return (
                    <TableRow key={r.residualCode}>
                      <TableCell>
                        <ResidualCodeText code={r.residualCode} />
                      </TableCell>
                      <TableCell>
                        {/* Read-only: shows what the row holds, including a stored value that is
                            neither Loans nor Leases, rather than hiding it behind "Not set". */}
                        {r.residualType ? (
                          <span className="text-sm">{r.residualType}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not set</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {pendingProposal ? (
                          <Badge
                            variant="outline"
                            className="border-primary/50 text-[10px] uppercase tracking-[0.1em] text-primary"
                            title="A change to this residual code is awaiting review."
                          >
                            Pending: {pendingProposal.newResidualType}
                          </Badge>
                        ) : (
                          canPropose && (
                            <Button size="sm" variant="ghost" onClick={() => setResidualTarget(r)}>
                              Propose change
                            </Button>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between pt-3 text-xs text-muted-foreground">
            <span>
              {residualTotal === 0
                ? "0 of 0"
                : `${residualOffset + 1}–${Math.min(residualOffset + PAGE_SIZE, residualTotal)} of ${residualTotal}`}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={residualOffset === 0}
                onClick={() => setResidualOffset((o) => Math.max(0, o - PAGE_SIZE))}
              >
                Prev
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={residualOffset + PAGE_SIZE >= residualTotal}
                onClick={() => setResidualOffset((o) => o + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <ResidualCodeApprovalQueue
        proposals={residualPending}
        onReview={onResidualReview}
        canDecide={canDecide}
      />

      <ResidualCodeLog />

      <ResidualCodeDialog
        open={!!residualTarget}
        onOpenChange={(o) => !o && setResidualTarget(null)}
        row={residualTarget}
        propose={(input) => proposeResidual.mutateAsync(input)}
        onSubmitted={(m) => setToast(m)}
      />

      <AddResidualCodeDialog
        open={addResidualOpen}
        onOpenChange={setAddResidualOpen}
        propose={(input) => proposeResidual.mutateAsync(input)}
        onSubmitted={(m) => setToast(m)}
      />

      <InvestorCodeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        listings={allLiveListingsQ.data?.listings ?? []}
        editListing={editListing}
        propose={(input) => propose.mutateAsync(input)}
        onSubmitted={(m) => setToast(m)}
      />

      <RetireInvestorCodeDialog
        open={!!retireTarget}
        onOpenChange={(o) => !o && setRetireTarget(null)}
        listing={retireTarget}
        propose={(input) => propose.mutateAsync(input)}
        onSubmitted={(m) => setToast(m)}
      />
    </ManageLimitsShell>
  );
}

function InvestorListingApprovalQueue({
  proposals,
  onReview,
  canDecide,
}: {
  proposals: InvestorListingProposal[];
  onReview: (
    p: InvestorListingProposal,
    decision: "APPROVED" | "REJECTED",
    notes: string,
  ) => Promise<unknown>;
  /** Contributor and above. A reviewer still sees the queue -- they raised what is in
   *  it -- but cannot approve or reject. */
  canDecide: boolean;
}) {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  async function act(p: InvestorListingProposal, decision: "APPROVED" | "REJECTED") {
    const note = (notes[p.proposalId] ?? "").trim();
    if (!note) {
      setErrors((e) => ({ ...e, [p.proposalId]: "Review notes are required." }));
      return;
    }
    try {
      setBusy(p.proposalId);
      await onReview(p, decision, note);
      setNotes((n) => ({ ...n, [p.proposalId]: "" }));
      setErrors((e) => ({ ...e, [p.proposalId]: "" }));
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [p.proposalId]: err instanceof Error ? err.message : "Review failed.",
      }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">
          Approval queue{proposals.length ? ` (${proposals.length})` : ""}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {proposals.length === 0 ? (
          <p className="text-sm text-muted-foreground">No proposals awaiting review.</p>
        ) : (
          <div className="space-y-4">
            {proposals.map((p) => (
              <div key={p.proposalId} className="rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 text-sm">
                    {/* The KIND leads, because it is what the reviewer is being asked to
                        decide: retiring a code and renaming one are different decisions. */}
                    <p className="font-medium">
                      {LISTING_KIND_LABEL[p.changeKind]} —{" "}
                      {p.changeKind === "RETIRE"
                        ? "retire"
                        : p.changeType === "ADD"
                          ? "set for"
                          : "change to"}{" "}
                      investor code{" "}
                      <span className="font-mono tabular-nums">{p.investorCode}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {listingChangeSummary(p)} · effective {p.effectiveStartDate} · proposed by{" "}
                      {p.proposedByRole}
                    </p>
                    {p.reason && <p className="mt-1 text-xs">{p.reason}</p>}
                    {p.changeKind === "RETIRE" && (
                      <p className="mt-1 text-[11px] text-amber">
                        Approving leaves this code with no live listing. Any capital source
                        mapping is untouched — the code keeps counting, it just stops having a
                        name here.
                      </p>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className="border-primary/50 text-[10px] uppercase tracking-[0.1em] text-primary"
                  >
                    Pending
                  </Badge>
                </div>

                {/* Deciding is the contributor's half of maker-checker. Backed by require_tier
                    on POST /investor-listing-proposals/{id}/review. */}
                {canDecide && (
                  <div className="mt-3 space-y-2">
                    <Textarea
                      rows={2}
                      placeholder="Review notes (required)"
                      value={notes[p.proposalId] ?? ""}
                      onChange={(e) =>
                        setNotes((n) => ({ ...n, [p.proposalId]: e.target.value }))
                      }
                    />
                    {errors[p.proposalId] && (
                      <p className="text-xs text-destructive">{errors[p.proposalId]}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={busy === p.proposalId}
                        onClick={() => act(p, "APPROVED")}
                      >
                        {busy === p.proposalId ? "Applying…" : "Approve"}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={busy === p.proposalId}
                        onClick={() => act(p, "REJECTED")}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InvestorListingLog() {
  // Self-contained, same shape as ContractValuationSettings.tsx's FairValueLog -- its own
  // offset state AND its own server-paginated fetch, so a page turn here is a real request for
  // just that page, not a slice of an array already in hand.
  const [offset, setOffset] = useState(0);
  const logQ = useInvestorListingProposals({ limit: PAGE_SIZE, offset });
  const total = logQ.data?.total ?? 0;
  const page = logQ.data?.proposals ?? [];

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">Investor change log</CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Investor code</TableHead>
              <TableHead>Change</TableHead>
              <TableHead>Effective from</TableHead>
              <TableHead>Proposed by</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Reviewed by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {total === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                  Nothing proposed yet.
                </TableCell>
              </TableRow>
            )}
            {page.map((p) => (
              <TableRow key={p.proposalId}>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[10px] uppercase tracking-[0.1em]",
                      p.status === "APPROVED"
                        ? "border-[color:var(--approve)]/50 text-[color:var(--approve)]"
                        : p.status === "REJECTED"
                          ? "border-destructive/50 text-destructive"
                          : "border-primary/50 text-primary",
                    )}
                  >
                    {/* An approved change is what put a row live, so the log reads in the
                        vocabulary of the record it created rather than of the paperwork. */}
                    {p.status === "APPROVED" ? "Live" : p.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className="whitespace-nowrap border-border text-[10px] uppercase tracking-[0.1em] text-muted-foreground"
                  >
                    {LISTING_KIND_LABEL[p.changeKind]}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono tabular-nums">{p.investorCode}</TableCell>
                <TableCell className="whitespace-nowrap">{listingChangeSummary(p)}</TableCell>
                <TableCell className="text-muted-foreground">{p.effectiveStartDate}</TableCell>
                <TableCell className="text-muted-foreground">{p.proposedByRole}</TableCell>
                <TableCell className="max-w-[220px] text-xs">{p.reason}</TableCell>
                <TableCell className="max-w-[220px] text-xs text-muted-foreground">
                  {p.reviewedByRole ? (
                    <>
                      {p.reviewedByRole}
                      {p.reviewNotes && <div className="text-[11px]">{p.reviewNotes}</div>}
                    </>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between px-4 pt-3 text-xs text-muted-foreground">
          <span>
            {total === 0 ? "0 of 0" : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
